// @client/components/profile-manager/EditModal.tsx
// Unified Single + Bulk Profile Editing Modal

import React, { useState, useEffect } from "react";
import { Check, User, Users } from "lucide-react";
import { Button } from "@client/components/ui/button";
import { Input } from "@client/components/ui/input";
import { Label } from "@client/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@client/components/ui/dialog";
import { Badge } from "@client/components/ui/badge";
import { Separator } from "@client/components/ui/separator";

// Import Core-Types direkt via @shared/types
import type { ProfileEditData, ProfileWithRelations } from "@shared/types";

export interface EditModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'single' | 'bulk';
  scopeLabel: string;
  initialData: ProfileEditData;
  onSubmit: (data: ProfileEditData) => void;
  loading: boolean;
  profile?: ProfileWithRelations;  // ✅ Core-Type mit Relations
  affectedCount?: number;
}

export interface EditData {
  name?: string;
  title?: string;
  company?: string;
  location?: string;
  email?: string;
  gender?: string;
  industry?: string;
  score?: number;
}

interface ValidationError {
  field: keyof EditData;
  message: string;
}

const validateEditData = (data: EditData, mode: 'single' | 'bulk'): ValidationError[] => {
  const errors: ValidationError[] = [];
  
  if (mode === 'single' && data.name !== undefined && (!data.name || data.name.trim().length === 0)) {
    errors.push({ field: 'name', message: 'Name is required' });
  }
  
  if (data.name && data.name.length > 255) {
    errors.push({ field: 'name', message: 'Name must be less than 255 characters' });
  }
  
  if (data.score !== undefined) {
    if (isNaN(data.score) || data.score < 0 || data.score > 100) {
      errors.push({ field: 'score', message: 'Score must be between 0 and 100' });
    }
  }
  
  if (data.title && data.title.length > 255) {
    errors.push({ field: 'title', message: 'Title must be less than 255 characters' });
  }
  
  if (data.company && data.company.length > 255) {
    errors.push({ field: 'company', message: 'Company must be less than 255 characters' });
  }
  
  if (data.location && data.location.length > 255) {
    errors.push({ field: 'location', message: 'Location must be less than 255 characters' });
  }
  
  if (data.email && data.email.length > 255) {
    errors.push({ field: 'email', message: 'Email must be less than 255 characters' });
  }
  
  if (data.email && data.email.trim() !== '') {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(data.email)) {
      errors.push({ field: 'email', message: 'Please enter a valid email address' });
    }
  }
  
  return errors;
};

export function EditModal({
  open,
  onOpenChange,
  mode,
  scopeLabel,
  initialData,
  onSubmit,
  loading,
  profile,
  affectedCount
}: EditModalProps) {
  
  const [formData, setFormData] = useState<EditData>(initialData);
  const [errors, setErrors] = useState<ValidationError[]>([]);
  const [isDirty, setIsDirty] = useState(false);
  const [dirtyFields, setDirtyFields] = useState<Set<keyof EditData>>(new Set());
  
  useEffect(() => {
    setFormData(initialData);
    setErrors([]);
    setIsDirty(false);
    setDirtyFields(new Set());
  }, [initialData, open]);
  
  const updateField = (field: keyof EditData, value: string | number | undefined) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setDirtyFields(prev => new Set([...prev, field]));
    setIsDirty(true);
    setErrors(prev => prev.filter(err => err.field !== field));
  };
  
  const handleSubmit = () => {
    const validationErrors = validateEditData(formData, mode);
    
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }
    
    let dataToSubmit: EditData;
    
    if (mode === 'bulk') {
      dataToSubmit = {};
      dirtyFields.forEach(field => {
        const value = formData[field];
        if (field!== undefined && value !== undefined && value !== '') {
          (dataToSubmit as any)[field] = value;
        }
      });
    } else {
      dataToSubmit = Object.fromEntries(
        Object.entries(formData).filter(([_, value]) => 
          value !== undefined && value !== ''
        )
      );
    }
    
    onSubmit(dataToSubmit);
  };
  
  const getFieldError = (field: keyof EditData) => {
    return errors.find(err => err.field === field)?.message;
  };
  
  const isSubmitDisabled = loading || !isDirty || errors.length > 0;
  
  const getModeIcon = () => mode === 'single' ? <User className="h-4 w-4" /> : <Users className="h-4 w-4" />;
  
  const getModeDescription = () => {
    if (mode === 'single') {
      return profile ? `Edit profile information for ${profile.name}` : 'Edit profile information';
    } else {
      return affectedCount 
        ? `Update ${affectedCount} selected profiles. Only changed fields will be updated.`
        : 'Update multiple profiles. Only changed fields will be updated.';
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {getModeIcon()}
            {mode === 'single' ? 'Edit Profile' : 'Bulk Edit Profiles'}
          </DialogTitle>
          <DialogDescription>
            {getModeDescription()}
          </DialogDescription>
          
          <div className="flex items-center gap-2 mt-2">
            <Badge variant="outline" className="text-xs">
              {scopeLabel}
            </Badge>
            {mode === 'bulk' && dirtyFields.size > 0 && (
              <Badge variant="secondary" className="text-xs">
                {dirtyFields.size} field{dirtyFields.size !== 1 ? 's' : ''} to update
              </Badge>
            )}
          </div>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="name" className="text-sm font-medium">
              Name {mode === 'single' && <span className="text-red-500">*</span>}
            </Label>
            <Input
              id="name"
              value={formData.name || ''}
              onChange={(e) => updateField('name', e.target.value)}
              placeholder={mode === 'single' ? "Enter full name" : "Leave empty to keep unchanged"}
              disabled={loading}
              className={getFieldError('name') ? 'border-red-500' : ''}
            />
            {getFieldError('name') && (
              <p className="text-sm text-red-500">{getFieldError('name')}</p>
            )}
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="title" className="text-sm font-medium">Title</Label>
            <Input
              id="title"
              value={formData.title || ''}
              onChange={(e) => updateField('title', e.target.value)}
              placeholder={mode === 'single' ? "Job title or position" : "Leave empty to keep unchanged"}
              disabled={loading}
              className={getFieldError('title') ? 'border-red-500' : ''}
            />
            {getFieldError('title') && (
              <p className="text-sm text-red-500">{getFieldError('title')}</p>
            )}
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="company" className="text-sm font-medium">Company</Label>
            <Input
              id="company"
              value={formData.company || ''}
              onChange={(e) => updateField('company', e.target.value)}
              placeholder={mode === 'single' ? "Company name" : "Leave empty to keep unchanged"}
              disabled={loading}
              className={getFieldError('company') ? 'border-red-500' : ''}
            />
            {getFieldError('company') && (
              <p className="text-sm text-red-500">{getFieldError('company')}</p>
            )}
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="location" className="text-sm font-medium">Location</Label>
            <Input
              id="location"
              value={formData.location || ''}
              onChange={(e) => updateField('location', e.target.value)}
              placeholder={mode === 'single' ? "City, Country" : "Leave empty to keep unchanged"}
              disabled={loading}
              className={getFieldError('location') ? 'border-red-500' : ''}
            />
            {getFieldError('location') && (
              <p className="text-sm text-red-500">{getFieldError('location')}</p>
            )}
          </div>
          
          <Separator />
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="gender" className="text-sm font-medium">Gender</Label>
              <Input
                id="gender"
                value={formData.gender || ''}
                onChange={(e) => updateField('gender', e.target.value)}
                placeholder={mode === 'single' ? "Optional" : "Leave empty to keep unchanged"}
                disabled={loading}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="score" className="text-sm font-medium">Score (0-100)</Label>
              <Input
                id="score"
                type="number"
                min="0"
                max="100"
                value={formData.score !== undefined ? formData.score : ''}
                onChange={(e) => {
                  const value = e.target.value;
                  updateField('score', value === '' ? undefined : parseInt(value));
                }}
                placeholder={mode === 'single' ? "0-100" : "Leave empty to keep unchanged"}
                disabled={loading}
                className={getFieldError('score') ? 'border-red-500' : ''}
              />
              {getFieldError('score') && (
                <p className="text-sm text-red-500">{getFieldError('score')}</p>
              )}
            </div>
          </div>
          
          {mode === 'bulk' && (
            <div className="text-sm text-muted-foreground bg-blue-50 p-3 rounded">
              <strong>Bulk Edit Tips:</strong>
              <ul className="mt-1 space-y-1 text-xs">
                <li>• Only fields you modify will be updated</li>
                <li>• Empty fields will be ignored (existing values preserved)</li>
                <li>• Changes apply to all selected profiles</li>
              </ul>
            </div>
          )}
        </div>
        
        <DialogFooter>
          <div className="flex items-center justify-between w-full">
            <div className="text-sm text-muted-foreground">
              {mode === 'bulk' && dirtyFields.size > 0 && (
                `${dirtyFields.size} field${dirtyFields.size !== 1 ? 's' : ''} will be updated`
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
                Cancel
              </Button>
              <Button 
                onClick={handleSubmit} 
                disabled={isSubmitDisabled}
                className="min-w-[80px]"
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Saving...
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Check className="h-3 w-3" />
                    {mode === 'single' ? 'Save' : 'Update All'}
                  </div>
                )}
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}